import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import HeatMap from '@uiw/react-heat-map';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { api, type DailyRollup, type Incident } from '@/lib/api';
import { dayBoundsInTz, detectTimeZone, localDateInTz } from '@/lib/tz';
import { cn } from '@/lib/cn';
import { KindIcon, KIND_LABEL, KIND_TONE } from '@/components/kind-icon';

export const Route = createFileRoute('/timeline')({
  component: TimelinePage,
});

// 1 = great day, 5 = terrible. Heatmap maps higher count to darker shade,
// which lines up: 5 (terrible) becomes the strongest red.
// 6 is a sentinel for "events logged but no rollup yet" — distinct blue
// so it can't be mistaken for a low rollup score.
const PANEL_COLORS: Record<number, string> = {
  0: 'hsl(var(--muted))',
  1: '#bbf7d0',
  2: '#fde68a',
  3: '#fdba74',
  4: '#fca5a5',
  5: '#f87171',
  6: '#93c5fd',
};
const INCIDENT_ONLY = 6;

// Normalize whatever date-ish string we got — API timestamps,
// @uiw/react-heat-map's re-emitted non-zero-padded "YYYY-M-D", etc —
// down to the YYYY-MM-DD that the rest of the page keys everything by.
function ymd(s: string): string {
  const m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (!m) return s.slice(0, 10);
  return `${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}`;
}

function TimelinePage() {
  const tz = React.useMemo(detectTimeZone, []);
  const today = React.useMemo(() => localDateInTz(new Date(), tz), [tz]);
  const startDate = React.useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 26 * 7 + 1);
    return d;
  }, []);
  const startLocal = React.useMemo(() => localDateInTz(startDate, tz), [startDate, tz]);
  const sinceIso = React.useMemo(
    () => dayBoundsInTz(startLocal, tz).sinceIso,
    [startLocal, tz],
  );

  const rollups = useQuery({
    queryKey: ['rollups', startLocal, today],
    queryFn: () => api.listRollups({ since: startLocal, until: today, limit: 200 }),
    staleTime: 60_000,
  });

  // Incidents in the same window — so days with logged events show up
  // on the heatmap even before the user submits a rollup.
  const incidentsRange = useQuery({
    queryKey: ['incidents-range', sinceIso],
    queryFn: () => api.listIncidents({ since: sinceIso, limit: 500 }),
    staleTime: 60_000,
  });

  const [selected, setSelected] = React.useState<string>(today);

  const rollupByDate = React.useMemo(() => {
    const map = new Map<string, DailyRollup>();
    for (const r of rollups.data?.items ?? []) map.set(ymd(r.local_date), r);
    return map;
  }, [rollups.data]);

  const incidentCountByDate = React.useMemo(() => {
    const map = new Map<string, number>();
    for (const i of incidentsRange.data?.items ?? []) {
      const day = localDateInTz(new Date(i.occurred_at), tz);
      map.set(day, (map.get(day) ?? 0) + 1);
    }
    return map;
  }, [incidentsRange.data, tz]);

  const heatValues = React.useMemo(() => {
    const days = new Map<string, number>();
    // Rollup score (1-5) is authoritative when present.
    for (const r of rollups.data?.items ?? []) {
      days.set(ymd(r.local_date), r.overall_score);
    }
    // Days with incidents but no rollup get a sentinel value mapped to
    // a distinct blue, so they aren't conflated with rollup score 1.
    for (const [day] of incidentCountByDate) {
      if (!days.has(day)) days.set(day, INCIDENT_ONLY);
    }
    return Array.from(days, ([date, count]) => ({
      date: date.replace(/-/g, '/'),
      count,
    }));
  }, [rollups.data, incidentCountByDate]);

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="text-2xl font-semibold tracking-tight">Timeline</h1>
        <p className="text-sm text-muted-foreground">
          Six months of daily rollups. Tap a cell to see that day's events.
        </p>
      </section>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Heatmap</CardTitle>
          <CardDescription>
            Green → red = rollup score (1 great, 5 terrible). Blue = events logged but no
            end-of-day rollup yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveHeatMap
            value={heatValues}
            startDate={startDate}
            selected={selected}
            onSelect={setSelected}
            hasIncidents={(iso) => (incidentCountByDate.get(iso) ?? 0) > 0}
            hasRollup={(iso) => rollupByDate.has(iso)}
          />
        </CardContent>
      </Card>

      <DayDetail
        date={selected}
        rollup={rollupByDate.get(selected) ?? null}
        tz={tz}
      />
    </div>
  );
}

type ResponsiveHeatMapProps = {
  value: { date: string; count: number }[];
  startDate: Date;
  selected: string;
  onSelect: (date: string) => void;
  hasIncidents: (iso: string) => boolean;
  hasRollup: (iso: string) => boolean;
};

function ResponsiveHeatMap({
  value,
  startDate,
  selected,
  onSelect,
  hasIncidents,
  hasRollup,
}: ResponsiveHeatMapProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 27 columns (26 weeks + the current partial week) + ~30px for the
  // leading week-day labels. Pick the rect size that fits the measured
  // width; clamp so it stays touchable on mobile and reasonable on
  // desktop.
  const COLS = 27;
  const LABEL_W = 30;
  const space = 3;
  const rectSize = width === 0 ? 12 : Math.max(8, Math.min(16, Math.floor((width - LABEL_W) / COLS) - space));

  return (
    <div ref={containerRef} className="w-full">
      {width > 0 && (
        <HeatMap
          value={value}
          startDate={startDate}
          endDate={new Date()}
          width={width}
          rectSize={rectSize}
          space={space}
          panelColors={PANEL_COLORS}
          legendCellSize={0}
          rectProps={{ rx: 2.5 }}
          rectRender={(props, data) => {
            // @uiw/react-heat-map parses our zero-padded "YYYY/MM/DD"
            // into a Date and re-emits without leading zeros, so the
            // raw replace produced "YYYY-M-D" and missed map lookups.
            const isoDate = ymd(data.date.replace(/\//g, '-'));
            const isSelected = isoDate === selected;
            const incidents = hasIncidents(isoDate);
            const rollup = hasRollup(isoDate);
            let title = isoDate;
            if (rollup) title += ` — overall ${data.count}/5`;
            else if (incidents) title += ' — event(s) logged, no rollup yet';
            else title += ' — no data';
            return (
              <rect
                {...props}
                stroke={isSelected ? 'currentColor' : 'transparent'}
                strokeWidth={isSelected ? 1.5 : 0}
                style={{ cursor: 'pointer', ...props.style }}
                onClick={() => onSelect(isoDate)}
              >
                <title>{title}</title>
              </rect>
            );
          }}
        />
      )}
    </div>
  );
}

function DayDetail({
  date,
  rollup,
  tz,
}: {
  date: string;
  rollup: DailyRollup | null;
  tz: string;
}) {
  const bounds = React.useMemo(() => dayBoundsInTz(date, tz), [date, tz]);
  const incidents = useQuery({
    queryKey: ['incidents-day', date, tz],
    queryFn: () =>
      api.listIncidents({ since: bounds.sinceIso, until: bounds.untilIso, limit: 200 }),
    staleTime: 60_000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{date}</CardTitle>
        <CardDescription>
          {rollup ? (
            <span>
              Overall {rollup.overall_score}/5 · ectopic {rollup.ectopic_score} · reflux{' '}
              {rollup.reflux_score} · IBS {rollup.ibs_score} · pain {rollup.pain_score}
              {rollup.note ? ` — "${rollup.note}"` : ''}
            </span>
          ) : (
            'No daily rollup submitted.'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {incidents.isPending && (
          <p className="text-sm text-muted-foreground">Loading events…</p>
        )}
        {incidents.error && (
          <p className="text-sm text-destructive">
            {(incidents.error as Error).message}
          </p>
        )}
        {incidents.data && incidents.data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">No events logged on this day.</p>
        )}
        {incidents.data?.items.map((i) => <EventRow key={i.id} incident={i} tz={tz} />)}
      </CardContent>
    </Card>
  );
}

function EventRow({ incident, tz }: { incident: Incident; tz: string }) {
  const time = new Date(incident.occurred_at).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
  return (
    <div className="flex items-start gap-3 rounded-md border border-border p-3">
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          KIND_TONE[incident.kind],
        )}
      >
        <KindIcon kind={incident.kind} className="h-3 w-3" />
        {KIND_LABEL[incident.kind]}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium tabular-nums">{time}</span>
          {incident.severity != null && (
            <span className="text-xs text-muted-foreground">
              severity {incident.severity}/5
            </span>
          )}
        </div>
        <EventExtras incident={incident} />
        {incident.note && (
          <p className="mt-1 text-sm text-muted-foreground">{incident.note}</p>
        )}
      </div>
    </div>
  );
}

function EventExtras({ incident }: { incident: Incident }) {
  const parts: string[] = [];
  if (incident.kind === 'ectopic' && incident.ectopic_count != null) {
    parts.push(`${incident.ectopic_count} beats`);
  }
  if (incident.kind === 'reflux') {
    if (incident.reflux_location) parts.push(incident.reflux_location);
    if (incident.woke_from_sleep) parts.push('woke from sleep');
  }
  if (incident.kind === 'ibs') {
    if (incident.ibs_kind) parts.push(incident.ibs_kind);
    if (incident.bristol_scale != null) parts.push(`Bristol ${incident.bristol_scale}`);
  }
  if (incident.kind === 'pain' && incident.pain_location) {
    parts.push(incident.pain_location);
  }
  const tagList: string[] = [];
  const tags = incident.tags as Record<string, unknown>;
  if (tags.caffeine) tagList.push('caffeine');
  if (tags.big_meal) tagList.push('big meal');
  if (tags.slept_well) tagList.push('slept well');
  if (typeof tags.stress === 'number') tagList.push(`stress ${tags.stress}`);
  const all = [...parts, ...tagList];
  if (all.length === 0) return null;
  return <p className="text-xs text-muted-foreground">{all.join(' · ')}</p>;
}
